import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import ChatUI from "../components/chat/ChatUI";
import { chatApi } from "../api/chatApi";
import { sessionRoomApi } from "../api/sessionRoomApi";
import { sessionsApi } from "../api/sessionsApi";
import { useAuth } from "../auth/authStore";
import { readAuthToken } from "../auth/authStorage";
import { parseChatContentCardPayload, normalizeChatContentCard } from "../chat/contentPayloads";
import { API_BASE_URL } from "../config/env";
import { normalizeMalformedAegisUrl } from "../utils/aegisUrl";
import { useNotifications } from "../notifications/notificationsStore";
import { useToast } from "../ui/toast/ToastProvider";
import Modal from "../components/ui/Modal";

function displayRole(role) {
  const value = String(role || "").toUpperCase();
  if (value === "ADMIN") return "Администратор";
  if (value === "PSYCHOLOGIST") return "Психолог";
  if (value === "MODERATOR") return "Модератор";
  if (value === "CLIENT") return "Клиент";
  return role || "Пользователь";
}

function parseDate(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === 0) return Boolean(value);
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  return null;
}

function formatDateTime(raw) {
  const d = parseDate(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatDateLabel(raw) {
  const d = parseDate(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(d);
}

function formatTime(raw) {
  const d = parseDate(raw);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "Размер не указан";
  if (value < 1024) return `${value} Б`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} КБ`;
  return `${(value / (1024 * 1024)).toFixed(1)} МБ`;
}

function initials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "??";
}

const CONFERENCE_JOIN_EARLY_MS = 30 * 60 * 1000;
const MSK_UTC_OFFSET_HOURS = 3;
const REVIEW_PROMPT_STORAGE_PREFIX = "conference-review-prompt:v1:";
const REVIEW_PROMPT_STATE_DISMISSED = "dismissed";
const REVIEW_PROMPT_STATE_SUBMITTED = "submitted";
const CONFERENCE_CLOSED_STATUSES = new Set([
  "CLOSED",
  "ENDED",
  "COMPLETED",
  "COMPLETE",
  "FINISHED",
  "CANCELLED",
  "CANCELED",
]);
const CONFERENCE_CANCELLED_STATUSES = new Set([
  "CANCELLED",
  "CANCELED",
  "SESSION_CANCELLED",
  "SESSION_CANCELED",
  "BOOKING_CANCELLED",
]);
const CONFERENCE_COMPLETED_STATUSES = new Set([
  "ENDED",
  "COMPLETED",
  "COMPLETE",
  "DONE",
  "FINISHED",
]);
const CONFERENCE_CLOSED_ONLY_STATUSES = new Set([
  "CLOSED",
  "ROOM_CLOSED",
  "SESSION_ROOM_CLOSED",
]);

function pickNestedText(obj, keys, maxDepth = 3) {
  if (!obj || typeof obj !== "object" || maxDepth < 0) return "";
  for (const key of keys) {
    const value = obj?.[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  const nestedKeys = ["sessionRoom", "session_room", "room", "booking", "appointment", "session", "conference", "meta"];
  for (const nestedKey of nestedKeys) {
    const nested = obj?.[nestedKey];
    if (nested && typeof nested === "object") {
      const value = pickNestedText(nested, keys, maxDepth - 1);
      if (value) return value;
    }
  }
  return "";
}


function collectNestedTexts(obj, keys, maxDepth = 3, acc = []) {
  if (!obj || typeof obj !== "object" || maxDepth < 0) return acc;
  for (const key of keys) {
    const value = obj?.[key];
    if (value != null && String(value).trim()) acc.push(String(value).trim());
  }
  const nestedKeys = ["sessionRoom", "session_room", "room", "booking", "appointment", "session", "conference", "meta"];
  for (const nestedKey of nestedKeys) {
    const nested = obj?.[nestedKey];
    if (nested && typeof nested === "object") collectNestedTexts(nested, keys, maxDepth - 1, acc);
  }
  return acc;
}

function pickNestedBoolean(obj, keys, maxDepth = 3) {
  if (!obj || typeof obj !== "object" || maxDepth < 0) return null;
  for (const key of keys) {
    const value = normalizeBoolean(obj?.[key]);
    if (value !== null) return value;
  }
  const nestedKeys = ["sessionRoom", "session_room", "room", "booking", "appointment", "session", "conference", "meta"];
  for (const nestedKey of nestedKeys) {
    const nested = obj?.[nestedKey];
    if (nested && typeof nested === "object") {
      const value = pickNestedBoolean(nested, keys, maxDepth - 1);
      if (value !== null) return value;
    }
  }
  return null;
}

function pickNestedNumber(obj, keys, maxDepth = 3) {
  if (!obj || typeof obj !== "object" || maxDepth < 0) return null;
  for (const key of keys) {
    const value = Number(obj?.[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  const nestedKeys = ["sessionRoom", "session_room", "room", "booking", "appointment", "session", "conference", "meta", "client", "psychologist", "therapist"];
  for (const nestedKey of nestedKeys) {
    const nested = obj?.[nestedKey];
    if (nested && typeof nested === "object") {
      const value = pickNestedNumber(nested, keys, maxDepth - 1);
      if (value != null) return value;
    }
  }
  return null;
}

function getRoomClientUserId(room) {
  return pickNestedNumber(room, [
    "clientUserId",
    "client_user_id",
    "clientId",
    "client_id",
    "participantUserId",
    "participant_user_id",
  ]);
}

function readReviewPromptStorageKey(roomToken) {
  const token = String(roomToken || "").trim();
  return token ? `${REVIEW_PROMPT_STORAGE_PREFIX}${token}` : "";
}

function readReviewPromptState(storageKey) {
  if (!storageKey || typeof window === "undefined") return "";
  try {
    const value = String(window.localStorage.getItem(storageKey) || "").trim().toLowerCase();
    if (value === REVIEW_PROMPT_STATE_DISMISSED || value === REVIEW_PROMPT_STATE_SUBMITTED) return value;
  } catch {
    // no-op
  }
  return "";
}

function saveReviewPromptState(storageKey, value) {
  if (!storageKey || typeof window === "undefined") return;
  try {
    if (!value) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, value);
  } catch {
    // no-op
  }
}

function resolveReviewErrorMessage(error) {
  const message = String(error?.message || "").toUpperCase();
  if (message.includes("SESSION_NOT_COMPLETED_FOR_REVIEW")) return "Оставить отзыв можно только после завершения сессии.";
  if (message.includes("REVIEW_REQUIRES_PAID_SESSION")) return "Отзыв можно оставить только после оплаченной сессии.";
  if (message.includes("REVIEW_BOOKING_NOT_ELIGIBLE")) return "Для этой записи пока нельзя оставить отзыв.";
  if (message.includes("REVIEW_SELF_NOT_ALLOWED") || message.includes("PSYCHOLOGIST_CANNOT_REVIEW_OWN_SESSION")) {
    return "Психолог не может оставить отзыв на собственную сессию.";
  }
  return String(error?.message || "Не удалось сохранить отзыв");
}

function isGuestInviteEnabled(room) {
  const kind = pickNestedText(room, [
    "kind",
    "conferenceKind",
    "conference_kind",
    "sessionKind",
    "session_kind",
    "inviteKind",
    "invite_kind",
    "createKind",
    "create_kind",
  ]).toUpperCase();
  if (kind.includes("INTERNAL") || kind.includes("PROJECT")) return false;
  if (kind === "EXTERNAL" || kind === "GUEST") return true;

  const explicit = pickNestedBoolean(room, [
    "guestJoinEnabled",
    "guest_join_enabled",
    "externalJoinEnabled",
    "external_join_enabled",
  ]);
  return explicit === true;
}

function collectConferenceStateTokens(room) {
  return collectNestedTexts(room, [
    "status",
    "state",
    "roomStatus",
    "room_status",
    "sessionStatus",
    "session_status",
    "bookingStatus",
    "booking_status",
    "conferenceStatus",
    "conference_status",
    "sessionRoomStatus",
    "session_room_status",
    "aegisRoomStatus",
    "aegis_room_status",
    "aegisStatus",
    "aegis_status",
    "joinBlockedReason",
    "join_blocked_reason",
    "aegisJoinBlockedReason",
    "aegis_join_blocked_reason",
  ]).map((status) => String(status || "").trim().toUpperCase()).filter(Boolean);
}

function getConferenceTerminalState(room) {
  const tokens = collectConferenceStateTokens(room);
  if (tokens.some((status) => CONFERENCE_CANCELLED_STATUSES.has(status))) return "cancelled";
  if (tokens.some((status) => CONFERENCE_COMPLETED_STATUSES.has(status))) return "completed";
  if (tokens.some((status) => CONFERENCE_CLOSED_ONLY_STATUSES.has(status))) return "closed";
  if (tokens.some((status) => CONFERENCE_CLOSED_STATUSES.has(status))) return "closed";
  return "";
}

function isConferenceEnded(room) {
  return Boolean(getConferenceTerminalState(room));
}

function getConferenceStartAt(room) {
  return parseDate(
    pickNestedText(room, [
      "sessionStartAt",
      "session_start_at",
      "startAt",
      "start_at",
      "startedAt",
      "started_at",
      "startTime",
      "start_time",
      "scheduledAt",
      "scheduled_at",
      "scheduledStartAt",
      "scheduled_start_at",
      "bookingStartAt",
      "booking_start_at",
      "dateTime",
      "date_time",
      "date",
    ])
  );
}

function isConferenceOpenBySchedule(room, now = Date.now()) {
  if (!room || isConferenceEnded(room)) return false;

  const availableFrom = parseDate(room?.aegisJoinAvailableFromAt);
  if (availableFrom && now >= availableFrom.getTime()) return true;

  const startAt = getConferenceStartAt(room);
  if (!startAt) return false;
  return now >= startAt.getTime() - CONFERENCE_JOIN_EARLY_MS;
}

function applyConferenceJoinAvailability(room) {
  if (!room || typeof room !== "object") return room;
  if (isConferenceEnded(room)) {
    return { ...room, aegisJoinAvailableNow: false };
  }
  if (isConferenceOpenBySchedule(room)) {
    return {
      ...room,
      aegisJoinAvailableNow: true,
      aegisJoinBlockedReason: "",
      aegisJoinBlockedMessage: "",
    };
  }
  return room;
}

function isConferenceJoinAvailable(room) {
  if (!room || isConferenceEnded(room)) return false;
  return Boolean(room?.aegisJoinAvailableNow || isConferenceOpenBySchedule(room));
}

function getDurationLabel(startAt, endAt) {
  const start = parseDate(startAt);
  const end = parseDate(endAt);
  if (!start || !end) return "—";
  const minutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  if (!minutes) return "—";
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
  }
  return `${minutes} мин`;
}

function getDurationMinutes(startAt, endAt, fallback = 50) {
  const start = parseDate(startAt);
  const end = parseDate(endAt);
  if (!start || !end) return fallback;
  const minutes = Math.max(20, Math.min(180, Math.round((end.getTime() - start.getTime()) / 60000)));
  return minutes || fallback;
}

function toMoscowDateTimeInputValue(date) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const msk = new Date(value.getTime() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  const two = (n) => String(n).padStart(2, "0");
  const y = msk.getUTCFullYear();
  const m = two(msk.getUTCMonth() + 1);
  const d = two(msk.getUTCDate());
  const hh = two(msk.getUTCHours());
  const mm = two(msk.getUTCMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function moscowDateTimeInputToIso(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  const utcMs = Date.UTC(year, month - 1, day, hour - MSK_UTC_OFFSET_HOURS, minute, 0, 0);
  const asUtc = new Date(utcMs);
  if (Number.isNaN(asUtc.getTime())) return null;

  const check = toMoscowDateTimeInputValue(asUtc);
  if (check !== raw) return null;
  return asUtc.toISOString();
}

function splitLocalDateTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return { datePart: "", timePart: "" };
  const [datePart = "", timePartRaw = ""] = raw.split("T");
  return {
    datePart: String(datePart || "").trim(),
    timePart: String(timePartRaw || "").trim().slice(0, 5),
  };
}

function normalizeRuDateDraft(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function toIsoDatePartFromRuDraft(value) {
  const normalized = normalizeRuDateDraft(value);
  const match = normalized.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return "";

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return "";
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2020 || year > 2100) return "";

  const utcMs = Date.UTC(year, month - 1, day, 12, 0, 0, 0);
  const probe = new Date(utcMs);
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() + 1 !== month || probe.getUTCDate() !== day) return "";

  const two = (n) => String(n).padStart(2, "0");
  return `${year}-${two(month)}-${two(day)}`;
}

function toRuDatePartFromIsoDatePart(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function normalizeRuTimeDraft(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function toNormalizedTimePart(value) {
  const normalized = normalizeRuTimeDraft(value);
  const match = normalized.match(/^(\d{2}):(\d{2})$/);
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return "";
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return "";
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function formatMoscowDateTimeLabel(value) {
  const iso = moscowDateTimeInputToIso(value);
  if (!iso) return "";
  const utcDate = new Date(iso);
  if (Number.isNaN(utcDate.getTime())) return "";
  const mskDate = new Date(utcDate.getTime() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  const dateLabel = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(mskDate);
  const timeLabel = new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(mskDate);
  return `${dateLabel}, ${timeLabel} МСК`;
}

function extractConferenceUpdateIdCandidates(room, routeBookingId = "") {
  const source = room && typeof room === "object" ? room : {};
  const booking = source.booking && typeof source.booking === "object" ? source.booking : {};
  const nestedRoom = source.room && typeof source.room === "object" ? source.room : {};
  const candidates = [
    source.groupSessionId,
    source.group_session_id,
    source.sessionId,
    source.session_id,
    booking.groupSessionId,
    booking.group_session_id,
    booking.sessionId,
    booking.session_id,
    source.id,
    source.bookingId,
    source.booking_id,
    booking.id,
    booking.bookingId,
    booking.booking_id,
    nestedRoom.groupSessionId,
    nestedRoom.group_session_id,
    nestedRoom.sessionId,
    nestedRoom.session_id,
    nestedRoom.id,
    nestedRoom.bookingId,
    nestedRoom.booking_id,
    routeBookingId,
  ];
  const unique = [];
  const seen = new Set();
  for (const value of candidates) {
    const id = String(value || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  return unique;
}

function extractBookingReferenceCandidates(room, routeBookingId = "") {
  const source = room && typeof room === "object" ? room : {};
  const booking = source.booking && typeof source.booking === "object" ? source.booking : {};
  const nestedRoom = source.room && typeof source.room === "object" ? source.room : {};
  const candidates = [
    source.bookingId,
    source.booking_id,
    booking.id,
    booking.bookingId,
    booking.booking_id,
    nestedRoom.bookingId,
    nestedRoom.booking_id,
    routeBookingId,
  ];
  const unique = [];
  const seen = new Set();
  for (const value of candidates) {
    const id = String(value || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  return unique;
}

function extractMaterials(room) {
  const sources = [room?.materials, room?.files, room?.attachments, room?.meetingMaterials, room?.documents];
  const list = sources.find((entry) => Array.isArray(entry)) || [];
  return list
    .map((item, index) => ({
      id: item?.id || item?.uuid || item?.key || `material-${index}`,
      name: String(item?.name || item?.filename || item?.title || `Материал ${index + 1}`),
      url: String(item?.url || item?.downloadUrl || item?.href || ""),
      extension: String(item?.extension || item?.ext || item?.mimeType || "").replace(/^\./, ""),
      sizeLabel: formatFileSize(item?.size || item?.fileSize),
    }))
    .filter((item) => item.name);
}

function materialTypeLabel(resourceType) {
  const type = String(resourceType || "").toUpperCase();
  if (type === "FILE_REF") return "Файл";
  if (type === "PODCAST_EPISODE") return "Подкаст";
  if (type === "TEST_ASSIGNMENT") return "Тест";
  if (type === "LITERATURE_BOOK") return "Книга";
  if (type === "LINK") return "Ссылка";
  return "Материал";
}

function extractResourceMaterials(resources) {
  const list = Array.isArray(resources) ? resources : [];
  return list
    .map((item, index) => {
      const payload = item?.payload && typeof item.payload === "object" ? item.payload : {};
      const sessionFlow = payload?.sessionFlow && typeof payload.sessionFlow === "object" ? payload.sessionFlow : {};
      const id = item?.id || item?.resourceId || `resource-${index}`;
      const name = String(
        item?.title
        || payload?.fileName
        || payload?.title
        || payload?.testTitle
        || payload?.podcastTitle
        || payload?.author
        || `Материал ${index + 1}`
      ).trim();
      const url = String(
        item?.url
        || payload?.fileUrl
        || payload?.playbackUrl
        || payload?.buyUrl
        || sessionFlow?.webPath
        || ""
      ).trim();
      const mimeType = String(payload?.mimeType || "").trim();
      const extFromName = String(name).includes(".") ? String(name).split(".").pop() : "";
      const extFromMime = String(mimeType).includes("/") ? String(mimeType).split("/").pop() : "";
      return {
        id: `resource-${id}`,
        name,
        url,
        extension: extFromName || extFromMime || materialTypeLabel(item?.resourceType),
        sizeLabel: payload?.fileSizeBytes ? formatFileSize(payload.fileSizeBytes) : `Из встречи · ${materialTypeLabel(item?.resourceType)}`,
      };
    })
    .filter((item) => item.name);
}

function chatCardLabel(type) {
  const value = String(type || "").toLowerCase();
  if (value.includes("podcast") || value === "episode") return "Подкаст";
  if (value.includes("literature") || value === "book") return "Книга";
  if (value.includes("test")) return "Тест";
  return "Рекомендация";
}

function extractChatMaterialsFromMessages(messages) {
  const rows = Array.isArray(messages) ? messages : [];
  const out = [];

  rows.forEach((row, index) => {
    const message = row && typeof row === "object" && row.message && typeof row.message === "object" ? row.message : row;
    if (!message || typeof message !== "object") return;

    const messageId = String(message.id || message.messageId || message.message_id || `m-${index}`);
    const attachments = Array.isArray(message.attachments)
      ? message.attachments
      : Array.isArray(message.files)
        ? message.files
        : [];

    attachments.forEach((attachment, attachmentIndex) => {
      const url = String(
        attachment?.url
        || attachment?.fileUrl
        || attachment?.downloadUrl
        || attachment?.file_url
        || attachment?.download_url
        || ""
      ).trim();
      const name = String(
        attachment?.name
        || attachment?.filename
        || attachment?.originalName
        || attachment?.original_name
        || `Вложение ${attachmentIndex + 1}`
      ).trim();
      const mimeType = String(attachment?.mime || attachment?.contentType || attachment?.type || "").trim();
      const extFromName = name.includes(".") ? name.split(".").pop() : "";
      const extFromMime = mimeType.includes("/") ? mimeType.split("/").pop() : "";
      const size = attachment?.size ?? attachment?.bytes ?? null;

      out.push({
        id: `chat-${messageId}-a-${attachment?.id || attachmentIndex}`,
        name,
        url,
        extension: extFromName || extFromMime || "Файл",
        sizeLabel: Number.isFinite(Number(size)) && Number(size) > 0
          ? formatFileSize(Number(size))
          : "Из чата · Файл",
      });
    });

    const rawText = String(message.text || message.content || message.message || message.body || "").trim();
    const inlineCard = parseChatContentCardPayload(rawText);
    const objectCard = message.contentCard && typeof message.contentCard === "object"
      ? normalizeChatContentCard(message.contentCard)
      : message.content_card && typeof message.content_card === "object"
        ? normalizeChatContentCard(message.content_card)
        : null;
    const card = inlineCard || objectCard;

    if (card) {
      out.push({
        id: `chat-${messageId}-card`,
        name: String(card.title || card.label || "Рекомендация").trim(),
        url: String(card.url || "").trim(),
        extension: String(card.label || chatCardLabel(card.type)).trim(),
        sizeLabel: `Из чата · ${String(card.label || chatCardLabel(card.type)).trim()}`,
      });
    }
  });

  return out.filter((item) => item && item.name);
}

function resolveMaterialUrl(rawUrl) {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
}

function resolveAvatarUrl(rawUrl) {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    if (!["http:", "https:", "data:", "blob:"].includes(url.protocol)) return "";
    return url.toString();
  } catch {
    return "";
  }
}

function isApiMaterialUrl(rawUrl, absoluteUrl) {
  const raw = String(rawUrl || "").trim();
  if (raw.startsWith("/api/")) return true;
  if (raw.includes("/api/chat/attachments/")) return true;
  try {
    const url = new URL(absoluteUrl || raw, window.location.origin);
    if (url.pathname.includes("/api/chat/attachments/")) return true;
    if (!url.pathname.startsWith("/api/")) return false;
    const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
    return url.origin === window.location.origin || url.origin === apiOrigin;
  } catch {
    return false;
  }
}

function extractFileNameFromUrl(rawUrl, fallback = "download") {
  const value = String(rawUrl || "").trim();
  if (!value) return fallback;
  try {
    const pathname = new URL(value, window.location.origin).pathname || "";
    const candidate = pathname.split("/").pop() || "";
    return decodeURIComponent(candidate) || fallback;
  } catch {
    return fallback;
  }
}

function resolveRelayParam(room, keys = []) {
  for (const key of keys) {
    if (!key) continue;
    const value = room?.[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function buildConferencePlayerUrl(baseUrl, room, locationSearch = "") {
  const base = normalizeMalformedAegisUrl(baseUrl);
  if (!base) return "";
  try {
    const url = new URL(base, window.location.origin);
    const pageParams = new URLSearchParams(String(locationSearch || ""));

    const relayUrl =
      pageParams.get("relay_url")
      || resolveRelayParam(room, ["relayUrl", "relay_url", "aegisRelayUrl", "aegis_relay_url"]);

    const relayEnabledRaw =
      pageParams.get("relay_transcript")
      || resolveRelayParam(room, ["relayTranscript", "relay_transcript", "aegisRelayTranscript", "aegis_relay_transcript"]);

    const relayEnabled = relayEnabledRaw === "1" || String(relayEnabledRaw).toLowerCase() === "true";

    if (!url.searchParams.has("relay_transcript") && (relayEnabled || relayUrl)) {
      url.searchParams.set("relay_transcript", "1");
    }

    const relayMap = [
      ["relay_url", relayUrl || pageParams.get("relay_url") || ""],
      ["relay_token", pageParams.get("relay_token") || resolveRelayParam(room, ["relayToken", "relay_token", "aegisRelayToken", "aegis_relay_token"])],
      ["relay_project", pageParams.get("relay_project") || resolveRelayParam(room, ["relayProject", "relay_project", "aegisRelayProject", "aegis_relay_project"])],
      ["relay_mode", pageParams.get("relay_mode") || resolveRelayParam(room, ["relayMode", "relay_mode", "aegisRelayMode", "aegis_relay_mode"])],
      ["relay_delay_ms", pageParams.get("relay_delay_ms") || resolveRelayParam(room, ["relayDelayMs", "relay_delay_ms", "aegisRelayDelayMs", "aegis_relay_delay_ms"])],
      ["relay_min_entries", pageParams.get("relay_min_entries") || resolveRelayParam(room, ["relayMinEntries", "relay_min_entries", "aegisRelayMinEntries", "aegis_relay_min_entries"])],
      ["relay_timeout_ms", pageParams.get("relay_timeout_ms") || resolveRelayParam(room, ["relayTimeoutMs", "relay_timeout_ms", "aegisRelayTimeoutMs", "aegis_relay_timeout_ms"])],
      ["relay_roles", pageParams.get("relay_roles") || resolveRelayParam(room, ["relayRoles", "relay_roles", "aegisRelayRoles", "aegis_relay_roles"])],
    ];

    relayMap.forEach(([key, value]) => {
      const text = String(value || "").trim();
      if (!text) return;
      if (!url.searchParams.has(key)) url.searchParams.set(key, text);
    });

    const hasInviteToken = url.searchParams.has("invite_token") || url.searchParams.has("invite") || url.searchParams.has("token");
    if (hasInviteToken) {
      [
        "session_token",
        "participant_session_token",
        "pst",
        "participant_id",
        "user_id",
        "role",
      ].forEach((key) => url.searchParams.delete(key));
    }

    return normalizeMalformedAegisUrl(url.toString());
  } catch {
    return base;
  }
}

function toGuestConferenceInviteUrl(rawLink, roomToken) {
  const token = String(roomToken || "").trim();
  if (!token) return String(rawLink || "").trim();
  const fallbackPath = `/conference/room/${encodeURIComponent(token)}`;
  const accessParamKeys = ["invite_token", "invite", "token"];
  const relayParamKeys = [
    "relay_transcript",
    "relay_url",
    "relay_token",
    "relay_project",
    "relay_mode",
    "relay_delay_ms",
    "relay_min_entries",
    "relay_timeout_ms",
    "relay_roles",
  ];

  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://burosoul.ru";
    const src = new URL(String(rawLink || "").trim() || fallbackPath, base);
    const guest = new URL(fallbackPath, base);

    for (const key of accessParamKeys) {
      const value = src.searchParams.get(key);
      if (value && !guest.searchParams.has(key)) guest.searchParams.set(key, value);
    }

    for (const key of relayParamKeys) {
      const value = src.searchParams.get(key);
      if (value && !guest.searchParams.has(key)) guest.searchParams.set(key, value);
    }

    return guest.toString();
  } catch {
    return String(rawLink || "").trim() || fallbackPath;
  }
}

function toProjectClientConferenceInviteUrl(rawLink, roomToken) {
  const token = String(roomToken || "").trim();
  if (!token) return String(rawLink || "").trim();
  const fallbackPath = `/dashboard/conferences/room/${encodeURIComponent(token)}?invite=1`;
  const relayParamKeys = [
    "relay_transcript",
    "relay_url",
    "relay_token",
    "relay_project",
    "relay_mode",
    "relay_delay_ms",
    "relay_min_entries",
    "relay_timeout_ms",
    "relay_roles",
  ];

  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://burosoul.ru";
    const src = new URL(String(rawLink || "").trim() || fallbackPath, base);
    const projectInvite = new URL(fallbackPath, base);
    projectInvite.searchParams.set("invite", "1");

    for (const key of relayParamKeys) {
      const value = src.searchParams.get(key);
      if (value && !projectInvite.searchParams.has(key)) projectInvite.searchParams.set(key, value);
    }

    return projectInvite.toString();
  } catch {
    return fallbackPath;
  }
}

function mergeAegisState(baseRaw, nextRaw) {
  const base = baseRaw && typeof baseRaw === "object" ? baseRaw : {};
  const next = nextRaw && typeof nextRaw === "object" ? nextRaw : {};
  const merged = {
    ...base,
    aegisJoinUrl: normalizeMalformedAegisUrl(next?.joinUrl || base?.aegisJoinUrl || ""),
    aegisPlayerUrl: normalizeMalformedAegisUrl(next?.playerUrl || base?.aegisPlayerUrl || ""),
    aegisTranscriptUrl: normalizeMalformedAegisUrl(next?.transcriptUrl || base?.aegisTranscriptUrl || ""),
    aegisTranscriptStatus: next?.transcriptStatus || base?.aegisTranscriptStatus || "",
    aegisJoinBlockedReason: next?.joinBlockedReason || base?.aegisJoinBlockedReason || "",
    aegisJoinBlockedMessage: next?.joinBlockedMessage || base?.aegisJoinBlockedMessage || "",
    aegisJoinAvailableFromAt: next?.joinAvailableFromAt || base?.aegisJoinAvailableFromAt || "",
    sessionRoomStatus: next?.sessionRoomStatus || base?.sessionRoomStatus || "",
    aegisRoomStatus: next?.aegisRoomStatus || base?.aegisRoomStatus || "",
  };

  if (next?.sessionRoomStatus || next?.status || next?.state) {
    merged.status = next?.sessionRoomStatus || next?.status || next?.state;
  }

  if (typeof next?.joinAvailableNow === "boolean") {
    merged.aegisJoinAvailableNow = next.joinAvailableNow;
  } else if (typeof base?.aegisJoinAvailableNow === "boolean") {
    merged.aegisJoinAvailableNow = base.aegisJoinAvailableNow;
  }

  return applyConferenceJoinAvailability(merged);
}

function IconCalendar() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="3" y="4.5" width="14" height="12" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6.5 3v3M13.5 3v3M3.5 8.5h13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="6.7" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 6.5v4l2.6 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconDotMenu() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="4.25" r="1.25" fill="currentColor" />
      <circle cx="10" cy="10" r="1.25" fill="currentColor" />
      <circle cx="10" cy="15.75" r="1.25" fill="currentColor" />
    </svg>
  );
}

function IconSessionVideo() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="6.5" width="12.5" height="11" rx="3.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="m16 10.5 4.6-2.7a.9.9 0 0 1 1.35.78v7a.9.9 0 0 1-1.35.78L16 13.5v-3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="9.5" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

function IconExternalConference() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9.2 14.8 7.1 17a3.15 3.15 0 0 1-4.45-4.45l2.9-2.9A3.15 3.15 0 0 1 10 9.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="m14.8 9.2 2.1-2.2a3.15 3.15 0 0 1 4.45 4.45l-2.9 2.9A3.15 3.15 0 0 1 14 14.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="m8.8 15.2 6.4-6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IconParticipants() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7 10.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm6 1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM2.5 15.2c.8-1.8 2.7-3 4.8-3 2.2 0 4 1.2 4.8 3M11.7 14.9c.5-.8 1.6-1.4 2.8-1.4 1.3 0 2.4.6 3 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="7" y="5" width="8" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 12V7a2 2 0 0 1 2-2h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 12.5l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.6 14a3.2 3.2 0 0 1 0-4.5l1.7-1.7a3.2 3.2 0 0 1 4.5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M13.4 6a3.2 3.2 0 0 1 0 4.5l-1.7 1.7a3.2 3.2 0 0 1-4.5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M6 3.5h5l3 3V15a2 2 0 0 1-2 2H6A2 2 0 0 1 4 15V5.5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M11 3.5v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3.5 6.5A2.5 2.5 0 0 1 6 4h2.4l1.5 1.6H14A2.5 2.5 0 0 1 16.5 8v6A2.5 2.5 0 0 1 14 16.5H6A2.5 2.5 0 0 1 3.5 14V6.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function IconTranscript() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="4" y="3.5" width="12" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 7.2h6M7 10h6M7 12.8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ParticipantAvatar({ name, avatarUrl, tone = "mint" }) {
  const [loadFailed, setLoadFailed] = React.useState(false);
  const resolvedUrl = !loadFailed ? String(avatarUrl || "").trim() : "";
  const toneClass = tone === "lavender"
    ? "conference-avatar--lavender"
    : tone === "gray"
      ? "conference-avatar--gray"
      : "";
  return (
    <div className={`conference-avatar conference-avatar--small ${toneClass}`}>
      {resolvedUrl ? (
        <img
          src={resolvedUrl}
          alt={name}
          loading="lazy"
          onError={() => setLoadFailed(true)}
        />
      ) : initials(name)}
    </div>
  );
}

export default function ConferenceRoom() {
  const { bookingId = "", roomToken = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { me, role } = useAuth();
  const { unreadNotificationsCount = 0, messageUnreadTotal = 0 } = useNotifications();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [roomLoading, setRoomLoading] = React.useState(true);
  const [roomError, setRoomError] = React.useState("");
  const [room, setRoom] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("overview");
  const [roomDrawer, setRoomDrawer] = React.useState("chat");

  const [notes, setNotes] = React.useState([]);
  const [notesLoading, setNotesLoading] = React.useState(false);
  const [resources, setResources] = React.useState([]);
  const [chatMaterials, setChatMaterials] = React.useState([]);
  const [noteDraft, setNoteDraft] = React.useState("");
  const [savingNote, setSavingNote] = React.useState(false);
  const [completingSession, setCompletingSession] = React.useState(false);
  const [editingNoteId, setEditingNoteId] = React.useState(null);
  const [deletingNoteId, setDeletingNoteId] = React.useState(null);
  const [inviteLink, setInviteLink] = React.useState("");
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const [inviteAudience, setInviteAudience] = React.useState("external");
  const [sessionActionsOpen, setSessionActionsOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editSaving, setEditSaving] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deletingConference, setDeletingConference] = React.useState(false);
  const [editTitle, setEditTitle] = React.useState("");
  const [editStartAt, setEditStartAt] = React.useState("");
  const [editDateDraft, setEditDateDraft] = React.useState("");
  const [editTimeDraft, setEditTimeDraft] = React.useState("");
  const [editDurationMinutes, setEditDurationMinutes] = React.useState(50);
  const [editKind, setEditKind] = React.useState("regular");
  const [reviewModalOpen, setReviewModalOpen] = React.useState(false);
  const [reviewRating, setReviewRating] = React.useState(0);
  const [reviewComment, setReviewComment] = React.useState("");
  const [reviewSubmitting, setReviewSubmitting] = React.useState(false);
  const [reviewPromptState, setReviewPromptState] = React.useState("");
  const [reviewAutoPrompted, setReviewAutoPrompted] = React.useState(false);
  const [stablePlayerUrl, setStablePlayerUrl] = React.useState("");
  const sessionActionsRef = React.useRef(null);

  const userName = me?.name || me?.fullName || me?.login || me?.email || "Пользователь";
  const handleBack = React.useCallback(() => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/dashboard/conferences", { replace: true });
  }, [navigate]);
  const resolvedRoomToken = String(room?.roomToken || roomToken || "").trim();
  const chatDialogId = String(room?.chatDialogId || room?.chatId || "").trim();
  const conferenceTerminalState = React.useMemo(() => getConferenceTerminalState(room), [room]);
  const conferenceEnded = Boolean(conferenceTerminalState);
  const conferenceLocked = !isConferenceJoinAvailable(room);
  const viewerUserId = React.useMemo(() => {
    const candidate = Number(me?.id ?? me?.userId ?? me?.uid ?? 0);
    return Number.isFinite(candidate) && candidate > 0 ? candidate : null;
  }, [me?.id, me?.uid, me?.userId]);
  const clientUserId = React.useMemo(() => getRoomClientUserId(room), [room]);
  const isViewerClient = String(role || "").toUpperCase() === "CLIENT" || (viewerUserId != null && clientUserId != null && viewerUserId === clientUserId);
  const guestInviteEnabled = React.useMemo(() => isGuestInviteEnabled(room), [room]);
  const canCompleteConference = Boolean(room?.psychologist);
  const canLeaveReview = Boolean(resolvedRoomToken) && conferenceEnded && isViewerClient;
  const reviewPromptStorageKey = React.useMemo(() => readReviewPromptStorageKey(resolvedRoomToken), [resolvedRoomToken]);
  const reviewActionLabel = reviewPromptState === REVIEW_PROMPT_STATE_SUBMITTED ? "Изменить отзыв" : "Оставить отзыв";
  const canReadTranscript = Boolean(room?.psychologist);
  const transcriptStatus = String(room?.aegisTranscriptStatus || "").toUpperCase();
  const transcriptUrl = String(room?.aegisTranscriptUrl || "").trim();
  const transcriptReady = transcriptStatus === "READY"
    || transcriptStatus === "FINALIZED"
    || transcriptStatus === "COMPLETED"
    || transcriptStatus === "DONE";
  const dynamicPlayerUrl = React.useMemo(
    () => buildConferencePlayerUrl(room?.aegisJoinUrl || room?.aegisPlayerUrl || "", room, location.search),
    [location.search, room]
  );
  const playerUrl = stablePlayerUrl || dynamicPlayerUrl;
  const canEditConference = Boolean(room) && !conferenceEnded && !roomLoading;
  const deleteBookingCandidates = React.useMemo(() => extractBookingReferenceCandidates(room, bookingId), [bookingId, room]);
  const canDeleteConference = deleteBookingCandidates.length > 0 && !conferenceEnded && !roomLoading;
  const editStartParts = React.useMemo(() => splitLocalDateTime(editStartAt), [editStartAt]);
  const editStartLabel = React.useMemo(() => formatMoscowDateTimeLabel(editStartAt), [editStartAt]);

  React.useEffect(() => {
    const nextDateDraft = toRuDatePartFromIsoDatePart(editStartParts.datePart);
    const nextTimeDraft = editStartParts.timePart;
    setEditDateDraft((prev) => (prev === nextDateDraft ? prev : nextDateDraft));
    setEditTimeDraft((prev) => (prev === nextTimeDraft ? prev : nextTimeDraft));
  }, [editStartParts.datePart, editStartParts.timePart]);

  React.useEffect(() => {
    if (!sessionActionsOpen) return undefined;
    const onPointerDown = (event) => {
      const node = sessionActionsRef.current;
      if (!node || node.contains(event.target)) return;
      setSessionActionsOpen(false);
    };
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      setSessionActionsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [sessionActionsOpen]);

  React.useEffect(() => {
    setReviewPromptState(readReviewPromptState(reviewPromptStorageKey));
    setReviewAutoPrompted(false);
    setReviewModalOpen(false);
    setReviewRating(0);
    setReviewComment("");
  }, [reviewPromptStorageKey]);

  React.useEffect(() => {
    setStablePlayerUrl("");
  }, [resolvedRoomToken]);

  React.useEffect(() => {
    if (!dynamicPlayerUrl || stablePlayerUrl) return;
    setStablePlayerUrl(dynamicPlayerUrl);
  }, [dynamicPlayerUrl, stablePlayerUrl]);

  React.useEffect(() => {
    if (!canLeaveReview) return;
    if (reviewAutoPrompted) return;
    if (reviewPromptState === REVIEW_PROMPT_STATE_DISMISSED || reviewPromptState === REVIEW_PROMPT_STATE_SUBMITTED) return;
    setReviewAutoPrompted(true);
    setReviewModalOpen(true);
  }, [canLeaveReview, reviewAutoPrompted, reviewPromptState]);

  const openEditModal = React.useCallback(() => {
    if (!canEditConference) return;
    const startRaw = room?.sessionStartAt || room?.startAt || room?.scheduledAt || "";
    const startDate = parseDate(startRaw) || new Date();
    const startInput = toMoscowDateTimeInputValue(startDate);
    const startParts = splitLocalDateTime(startInput);
    setEditTitle(String(room?.title || room?.groupTitle || room?.sessionTitle || "").trim());
    setEditStartAt(startInput);
    setEditDateDraft(toRuDatePartFromIsoDatePart(startParts.datePart));
    setEditTimeDraft(startParts.timePart);
    setEditDurationMinutes(getDurationMinutes(room?.sessionStartAt, room?.sessionEndAt, 50));
    setEditKind(guestInviteEnabled ? "external" : "regular");
    setSessionActionsOpen(false);
    setEditOpen(true);
  }, [canEditConference, guestInviteEnabled, room]);

  const onEditStartDateChange = React.useCallback((draftValue) => {
    const draft = normalizeRuDateDraft(draftValue);
    setEditDateDraft(draft);
    if (!draft) {
      setEditStartAt("");
      return;
    }
    const isoDatePart = toIsoDatePartFromRuDraft(draft);
    if (!isoDatePart) return;
    setEditStartAt((prev) => {
      const prevParts = splitLocalDateTime(prev);
      const nextTime = prevParts.timePart || "12:00";
      return `${isoDatePart}T${nextTime}`;
    });
  }, []);

  const onEditStartTimeChange = React.useCallback((draftValue) => {
    const draft = normalizeRuTimeDraft(draftValue);
    setEditTimeDraft(draft);
    const nextTime = toNormalizedTimePart(draft);
    if (!nextTime) return;
    setEditStartAt((prev) => {
      const prevParts = splitLocalDateTime(prev);
      const fallbackDate = splitLocalDateTime(toMoscowDateTimeInputValue(new Date())).datePart;
      const nextDate = prevParts.datePart || fallbackDate;
      return `${nextDate}T${nextTime}`;
    });
  }, []);

  const setEditDatePreset = React.useCallback((offsetDays) => {
    setEditStartAt((prev) => {
      const prevParts = splitLocalDateTime(prev);
      const baseMsk = new Date(Date.now() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
      baseMsk.setUTCSeconds(0, 0);
      baseMsk.setUTCDate(baseMsk.getUTCDate() + Number(offsetDays || 0));
      const nextDatePart = `${baseMsk.getUTCFullYear()}-${String(baseMsk.getUTCMonth() + 1).padStart(2, "0")}-${String(baseMsk.getUTCDate()).padStart(2, "0")}`;
      return `${nextDatePart}T${prevParts.timePart || "12:00"}`;
    });
  }, []);

  const setEditStartPreset = React.useCallback((offsetMinutes) => {
    const next = new Date(Date.now() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
    next.setUTCSeconds(0, 0);
    next.setUTCMinutes(next.getUTCMinutes() + Number(offsetMinutes || 0));
    setEditStartAt(`${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}T${String(next.getUTCHours()).padStart(2, "0")}:${String(next.getUTCMinutes()).padStart(2, "0")}`);
  }, []);

  const loadNotes = React.useCallback(async (token) => {
    const normalized = String(token || "").trim();
    if (!normalized) return;
    setNotesLoading(true);
    try {
      const page = await sessionRoomApi.listMyNotes(normalized, { page: 0, size: 100 });
      setNotes(Array.isArray(page.items) ? page.items : []);
    } catch (e) {
      toast.error(e?.message || "Не удалось загрузить заметки");
    } finally {
      setNotesLoading(false);
    }
  }, [toast]);

  React.useEffect(() => {
    let alive = true;
    setRoomLoading(true);
    setRoomError("");
    setRoom(null);
    setResources([]);
    setChatMaterials([]);

    (async () => {
      try {
        let item;
        if (roomToken) {
          try {
            item = await sessionRoomApi.getRoom(roomToken);
          } catch (roomError) {
            const status = Number(roomError?.status || 0);
            const message = String(roomError?.message || "");
            const forceInviteJoin = new URLSearchParams(location.search).get("invite") === "1";
            const canRetryViaInvite = forceInviteJoin || status === 403 || message.includes("SESSION_ROOM_ACCESS_DENIED");
            if (!canRetryViaInvite) throw roomError;
            await sessionRoomApi.joinByInvite(roomToken);
            item = await sessionRoomApi.getRoom(roomToken);
            toast.success("Вы присоединились к конференции по ссылке");
          }
        } else {
          item = await sessionRoomApi.openByBooking(bookingId);
        }
        if (!alive) return;
        const token = String(item?.roomToken || "").trim();
        let nextRoom = item;
        if (token) {
          const hasAegisPlayer = Boolean(item?.aegisJoinUrl || item?.aegisPlayerUrl);
          try {
            const status = await sessionRoomApi.getAegisStatus(token);
            nextRoom = mergeAegisState(nextRoom, status);
          } catch {
            // no-op
          }
          if (!hasAegisPlayer && !(nextRoom?.aegisJoinUrl || nextRoom?.aegisPlayerUrl)) {
            try {
              const synced = await sessionRoomApi.syncAegisStatus(token);
              nextRoom = mergeAegisState(nextRoom, synced);
            } catch {
              // no-op
            }
          }

          if (!alive) return;
          setRoom(applyConferenceJoinAvailability(nextRoom));

          if (roomToken && token !== roomToken) {
            navigate(`/dashboard/conferences/room/${encodeURIComponent(token)}`, { replace: true });
          }
          await Promise.all([
            loadNotes(token),
            sessionRoomApi.listResources(token, { page: 0, size: 200 })
              .then((page) => {
                if (!alive) return;
                setResources(Array.isArray(page?.items) ? page.items : []);
              })
              .catch(() => {
                if (!alive) return;
                setResources([]);
              }),
            (async () => {
              const dialogId = String(nextRoom?.chatDialogId || nextRoom?.chatId || "").trim();
              if (!dialogId) {
                if (alive) setChatMaterials([]);
                return;
              }
              const collected = [];
              let cursor = null;
              for (let i = 0; i < 4; i += 1) {
                const page = await chatApi.listMessages(dialogId, { limit: 50, cursor });
                const items = Array.isArray(page?.items) ? page.items : [];
                if (!items.length) break;
                collected.push(...items);
                if (!page?.nextCursor) break;
                cursor = page.nextCursor;
              }
              if (alive) {
                setChatMaterials(extractChatMaterialsFromMessages(collected));
              }
            })().catch(() => {
              if (!alive) return;
              setChatMaterials([]);
            }),
          ]);
          if (isGuestInviteEnabled(nextRoom)) {
            const link = await sessionRoomApi.getInviteLink(token).catch(() => null);
            if (alive) setInviteLink(String(link?.inviteUrl || ""));
          } else if (alive) {
            setInviteLink("");
          }
        } else {
          setRoom(applyConferenceJoinAvailability(nextRoom));
        }
      } catch (e) {
        if (!alive) return;
        setRoomError(e?.message || "Не удалось открыть конференцию");
      } finally {
        if (alive) setRoomLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [bookingId, loadNotes, location.search, navigate, roomToken, toast]);

  const onSaveNote = React.useCallback(async () => {
    const token = resolvedRoomToken;
    const content = String(noteDraft || "").trim();
    if (!token || !content || conferenceLocked) return;
    setSavingNote(true);
    try {
      if (editingNoteId) {
        await sessionRoomApi.updateMyNote(token, editingNoteId, content);
        toast.success("Заметка обновлена");
      } else {
        await sessionRoomApi.createMyNote(token, content);
        toast.success("Заметка добавлена");
      }
      setNoteDraft("");
      setEditingNoteId(null);
      await loadNotes(token);
    } catch (e) {
      toast.error(e?.message || "Не удалось сохранить заметку");
    } finally {
      setSavingNote(false);
    }
  }, [conferenceLocked, editingNoteId, loadNotes, noteDraft, resolvedRoomToken, toast]);

  const onDeleteNote = React.useCallback(async (noteId) => {
    const token = resolvedRoomToken;
    const id = String(noteId || "").trim();
    if (!token || !id || conferenceLocked) return;
    setDeletingNoteId(id);
    try {
      await sessionRoomApi.deleteMyNote(token, id);
      if (String(editingNoteId || "") === id) {
        setEditingNoteId(null);
        setNoteDraft("");
      }
      await loadNotes(token);
      toast.success("Заметка удалена");
    } catch (e) {
      toast.error(e?.message || "Не удалось удалить заметку");
    } finally {
      setDeletingNoteId(null);
    }
  }, [conferenceLocked, editingNoteId, loadNotes, resolvedRoomToken, toast]);

  const onCompleteSession = React.useCallback(async () => {
    const token = resolvedRoomToken;
    if (!token || conferenceEnded || completingSession || !canCompleteConference) return;
    setCompletingSession(true);
    try {
      const result = await sessionRoomApi.completeRoom(token, { waitExport: true, forceExport: true });
      const nextRoom = result?.room || null;
      if (nextRoom) {
        setRoom((prev) => mergeAegisState(prev, nextRoom));
      }

      try {
        const synced = await sessionRoomApi.syncAegisStatus(token);
        setRoom((prev) => mergeAegisState(prev, synced));
      } catch {
        // no-op
      }

      const exportState = String(result?.exportStatus?.status || result?.exportStatus || "").toUpperCase();
      if (exportState === "EXPORTED" || exportState === "READY" || exportState === "SUCCESS") {
        toast.success("Сессия завершена, транскрипция выгружена");
      } else {
        toast.success("Сессия завершена");
      }
    } catch (e) {
      toast.error(e?.message || "Не удалось завершить сессию");
    } finally {
      setCompletingSession(false);
    }
  }, [canCompleteConference, completingSession, conferenceEnded, resolvedRoomToken, toast]);

  const dismissReviewPrompt = React.useCallback(() => {
    if (reviewPromptStorageKey) {
      saveReviewPromptState(reviewPromptStorageKey, REVIEW_PROMPT_STATE_DISMISSED);
      setReviewPromptState(REVIEW_PROMPT_STATE_DISMISSED);
    }
    setReviewModalOpen(false);
  }, [reviewPromptStorageKey]);

  const onSubmitReview = React.useCallback(async () => {
    const token = resolvedRoomToken;
    if (!token || !canLeaveReview || reviewSubmitting) return;
    if (!Number.isFinite(reviewRating) || reviewRating < 1 || reviewRating > 5) {
      toast.error("Поставьте оценку от 1 до 5");
      return;
    }

    setReviewSubmitting(true);
    try {
      await sessionRoomApi.leaveReview(token, {
        rating: reviewRating,
        comment: reviewComment,
        isPublic: true,
      });
      if (reviewPromptStorageKey) {
        saveReviewPromptState(reviewPromptStorageKey, REVIEW_PROMPT_STATE_SUBMITTED);
      }
      setReviewPromptState(REVIEW_PROMPT_STATE_SUBMITTED);
      setReviewModalOpen(false);
      toast.success("Спасибо! Отзыв сохранен");
    } catch (error) {
      toast.error(resolveReviewErrorMessage(error));
    } finally {
      setReviewSubmitting(false);
    }
  }, [canLeaveReview, resolvedRoomToken, reviewComment, reviewPromptStorageKey, reviewRating, reviewSubmitting, toast]);

  const onSaveConferenceEdit = React.useCallback(async () => {
    const title = String(editTitle || "").trim();
    const startIso = moscowDateTimeInputToIso(editStartAt);
    const duration = Math.min(180, Math.max(20, Number(editDurationMinutes) || 50));
    const candidates = extractConferenceUpdateIdCandidates(room, bookingId);
    const isExternal = editKind === "external";

    if (!title) {
      toast.error("Укажите название сессии");
      return;
    }
    if (!startIso) {
      toast.error("Укажите корректное время старта");
      return;
    }
    if (!candidates.length) {
      toast.error("Не удалось определить идентификатор конференции");
      return;
    }

    const endIso = new Date(new Date(startIso).getTime() + duration * 60 * 1000).toISOString();
    setEditSaving(true);
    try {
      let updated = false;
      let lastError = null;
      const payload = {
        title,
        startDateTime: startIso,
        endDateTime: endIso,
        sessionFormat: "ONLINE",
        allowJoin: isExternal,
        capacityClients: 1,
        minCapacity: 1,
        priceAtTime: 0,
      };

      for (const id of candidates) {
        try {
          await sessionsApi.updateMyGroupSession(id, payload);
          updated = true;
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!updated) throw lastError || new Error("Не удалось обновить конференцию");

      if (resolvedRoomToken) {
        const refreshed = await sessionRoomApi.getRoom(resolvedRoomToken).catch(() => null);
        if (refreshed) setRoom(applyConferenceJoinAvailability(refreshed));
      } else if (bookingId) {
        const refreshed = await sessionRoomApi.openByBooking(bookingId).catch(() => null);
        if (refreshed) setRoom(applyConferenceJoinAvailability(refreshed));
      }

      if (isExternal && resolvedRoomToken) {
        const link = await sessionRoomApi.getInviteLink(resolvedRoomToken).catch(() => null);
        if (link?.inviteUrl) setInviteLink(String(link.inviteUrl));
      }

      setEditOpen(false);
      setSessionActionsOpen(false);
      toast.success("Конференция обновлена");
    } catch (e) {
      toast.error(e?.message || "Не удалось обновить конференцию");
    } finally {
      setEditSaving(false);
    }
  }, [bookingId, editDurationMinutes, editKind, editStartAt, editTitle, resolvedRoomToken, room, toast]);

  const refreshRoomSnapshot = React.useCallback(async () => {
    const token = String(resolvedRoomToken || "").trim();
    if (!token) return;
    try {
      const refreshed = await sessionRoomApi.getRoom(token);
      if (!refreshed) return;
      setRoom((prev) => applyConferenceJoinAvailability(mergeAegisState(prev, refreshed)));
    } catch {
      // keep current snapshot on transient errors
    }
  }, [resolvedRoomToken]);

  React.useEffect(() => {
    if (!resolvedRoomToken || conferenceEnded) return undefined;
    let alive = true;

    const onVisibleRefresh = () => {
      if (!alive) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void refreshRoomSnapshot();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("focus", onVisibleRefresh);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibleRefresh);
    }

    return () => {
      alive = false;
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onVisibleRefresh);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibleRefresh);
      }
    };
  }, [conferenceEnded, refreshRoomSnapshot, resolvedRoomToken]);

  const onDeleteConference = React.useCallback(async () => {
    if (!deleteBookingCandidates.length || deletingConference) return;
    setDeletingConference(true);
    try {
      let deleted = false;
      let lastError = null;
      for (const candidate of deleteBookingCandidates) {
        try {
          await sessionsApi.updateBookingStatus(candidate, "CANCELLED");
          deleted = true;
          break;
        } catch (error) {
          lastError = error;
        }
      }
      if (!deleted) throw lastError || new Error("Не удалось удалить конференцию");
      setDeleteConfirmOpen(false);
      setSessionActionsOpen(false);
      toast.success("Конференция удалена");
      navigate("/dashboard/conferences", { replace: true });
    } catch (e) {
      toast.error(e?.message || "Не удалось удалить конференцию");
    } finally {
      setDeletingConference(false);
    }
  }, [deleteBookingCandidates, deletingConference, navigate, toast]);

  const onCopyRoomToken = React.useCallback(async () => {
    if (!resolvedRoomToken) return;
    try {
      await navigator.clipboard.writeText(resolvedRoomToken);
      toast.success("Код комнаты скопирован");
    } catch {
      toast.error("Не удалось скопировать код комнаты");
    }
  }, [resolvedRoomToken, toast]);

  const ensureInviteSourceLink = React.useCallback(async () => {
    const token = resolvedRoomToken;
    if (!token) return "";
    let link = String(inviteLink || "").trim();
    if (link) return link;
    try {
      const fresh = await sessionRoomApi.getInviteLink(token);
      link = String(fresh?.inviteUrl || "").trim();
      if (link) setInviteLink(link);
    } catch {
      // fallback to token-based links below
    }
    return link;
  }, [inviteLink, resolvedRoomToken]);

  const onCopyInviteLink = React.useCallback(async () => {
    if (!resolvedRoomToken) {
      toast.error("Ссылка станет доступна после открытия комнаты");
      return;
    }
    setInviteModalOpen(true);
    if (inviteLink) return;
    await ensureInviteSourceLink();
  }, [ensureInviteSourceLink, inviteLink, resolvedRoomToken, toast]);

  const onCopySelectedInviteLink = React.useCallback(async () => {
    const token = resolvedRoomToken;
    if (!token) return;

    const sourceLink = String(inviteLink || "").trim() || await ensureInviteSourceLink();
    const targetLink = inviteAudience === "project"
      ? toProjectClientConferenceInviteUrl(sourceLink, token)
      : toGuestConferenceInviteUrl(sourceLink, token);
    if (!targetLink) {
      toast.error("Не удалось подготовить ссылку приглашения");
      return;
    }

    try {
      await navigator.clipboard.writeText(targetLink);
      toast.success("Ссылка приглашения скопирована");
      setInviteModalOpen(false);
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  }, [ensureInviteSourceLink, inviteAudience, inviteLink, resolvedRoomToken, toast]);

  const onOpenGuestInvite = React.useCallback(async () => {
    if (!guestInviteEnabled) return;
    const token = resolvedRoomToken;
    if (!token || typeof window === "undefined") return;
    try {
      let link = String(inviteLink || "").trim();
      if (!link) {
        const fresh = await sessionRoomApi.getInviteLink(token);
        link = String(fresh?.inviteUrl || "").trim();
        setInviteLink(link);
      }
      if (!link) {
        toast.error("Не удалось получить ссылку приглашения");
        return;
      }
      const guestLink = toGuestConferenceInviteUrl(link, token);
      if (!guestLink) {
        toast.error("Гостевая ссылка недоступна");
        return;
      }
      window.open(guestLink, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.message || "Не удалось открыть гостевую ссылку");
    }
  }, [guestInviteEnabled, inviteLink, resolvedRoomToken, toast]);

  const onDownloadMaterial = React.useCallback(async (event, item) => {
    const rawUrl = String(item?.url || "").trim();
    if (!rawUrl) return;
    const absoluteUrl = resolveMaterialUrl(rawUrl);
    if (!isApiMaterialUrl(rawUrl, absoluteUrl)) return;
    event.preventDefault();

    try {
      const token = readAuthToken();
      const res = await fetch(absoluteUrl, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = extractFileNameFromUrl(rawUrl, String(item?.name || "material"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error("Не удалось скачать материал");
    }
  }, [toast]);

  const transcriptHint = canReadTranscript
    ? transcriptUrl
      ? "Транскрипция отображается ниже"
      : transcriptReady
        ? "Транскрипция готова, но ссылка еще не получена"
        : "Транскрипция еще обрабатывается"
    : "Транскрипция доступна авторизованным участникам конференции";

  const sessionTitle = String(room?.title || room?.groupTitle || room?.sessionTitle || "").trim() || (room?.group ? "Групповая встреча" : "Индивидуальная сессия");
  const psychologistName = String(room?.psychologistName || room?.psychologist?.name || room?.therapistName || "Психолог").trim();
  const clientName = String(room?.clientName || room?.client?.name || room?.participantName || room?.withName || "Клиент").trim();
  const psychologistAvatarUrl = resolveAvatarUrl(
    room?.psychologistAvatarUrl
    || room?.psychologist_avatar_url
    || room?.psychologist?.avatarUrl
    || room?.psychologist?.avatar_url
    || room?.psychologist?.avatar
  );
  const clientAvatarUrl = resolveAvatarUrl(
    room?.clientAvatarUrl
    || room?.client_avatar_url
    || room?.client?.avatarUrl
    || room?.client?.avatar_url
    || room?.client?.avatar
  );
  const viewerAvatarUrl = resolveAvatarUrl(
    me?.avatarUrl
    || me?.avatar_url
    || me?.avatar
    || me?.photoUrl
    || me?.photo_url
    || me?.image
  );
  const durationLabel = getDurationLabel(room?.sessionStartAt, room?.sessionEndAt);
  const typeLabel = room?.group ? "Групповая сессия" : "Индивидуальная сессия";
  const guestInviteUrl = React.useMemo(() => {
    if (!resolvedRoomToken) return "";
    const raw = String(inviteLink || "").trim();
    return toGuestConferenceInviteUrl(raw, resolvedRoomToken);
  }, [inviteLink, resolvedRoomToken]);
  const projectInviteUrl = React.useMemo(() => {
    if (!resolvedRoomToken) return "";
    const raw = String(inviteLink || "").trim();
    return toProjectClientConferenceInviteUrl(raw, resolvedRoomToken);
  }, [inviteLink, resolvedRoomToken]);
  const selectedInviteUrl = inviteAudience === "project" ? projectInviteUrl : guestInviteUrl;
  const materials = React.useMemo(() => {
    const roomMaterials = extractMaterials(room);
    const resourceMaterials = extractResourceMaterials(resources);
    const fromChat = Array.isArray(chatMaterials) ? chatMaterials : [];
    const seen = new Set();
    return [...fromChat, ...resourceMaterials, ...roomMaterials].filter((item) => {
      const key = `${String(item?.name || "").trim().toLowerCase()}|${String(item?.url || "").trim().toLowerCase()}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [chatMaterials, resources, room]);
  const onConferenceChatMessagesChange = React.useCallback(({ messages }) => {
    const list = Array.isArray(messages) ? messages : [];
    setChatMaterials(extractChatMaterialsFromMessages(list));
  }, []);
  const participants = React.useMemo(() => {
    const rawMeId = me?.id ?? me?.userId ?? me?.uid ?? null;
    const meId = rawMeId == null ? null : Number(rawMeId);
    const psychologistUserId = Number(room?.psychologistUserId || 0) || null;
    const clientUserId = Number(room?.clientUserId || 0) || null;
    const isPrimaryPsychologist = Boolean(room?.psychologist) || (meId != null && psychologistUserId != null && meId === psychologistUserId);
    const isPrimaryClient = meId != null && clientUserId != null && meId === clientUserId;
    const list = [
      { id: "psychologist", name: psychologistName, role: "Психолог", badge: isPrimaryPsychologist ? "Вы" : "", tone: "mint", avatarUrl: psychologistAvatarUrl },
      { id: "client", name: clientName, role: room?.group ? "Участник" : "Клиент", badge: isPrimaryClient ? "Вы" : "", tone: "lavender", avatarUrl: clientAvatarUrl },
    ];
    if (!isPrimaryPsychologist && !isPrimaryClient) {
      const selfName = String(me?.name || me?.fullName || me?.login || me?.email || "Вы").trim();
      list.push({ id: "viewer-self", name: selfName, role: "Участник", badge: "Вы", tone: "gray", avatarUrl: viewerAvatarUrl });
    }
    if (room?.aegisTranscriptStatus) {
      list.push({ id: "assistant", name: "Aegis Assistant", role: "Ассистент", badge: "", tone: "gray", avatarUrl: "" });
    }
    return list;
  }, [
    clientAvatarUrl,
    clientName,
    me?.email,
    me?.fullName,
    me?.id,
    me?.login,
    me?.name,
    me?.uid,
    me?.userId,
    psychologistAvatarUrl,
    psychologistName,
    room?.aegisTranscriptStatus,
    room?.clientUserId,
    room?.group,
    room?.psychologist,
    room?.psychologistUserId,
    viewerAvatarUrl,
  ]);

  const latestNote = notes[0];

  const focusStatusTone = conferenceEnded ? "is-ended" : conferenceLocked ? "is-waiting" : "is-live";
  const focusSessionStatusLabel = conferenceTerminalState === "cancelled"
    ? "Сессия отменена"
    : conferenceTerminalState === "closed"
      ? "Комната закрыта"
      : conferenceEnded
        ? "Сессия завершена"
        : conferenceLocked
          ? "Ожидание входа"
          : "Соединение стабильно";
  const focusTimerLabel = conferenceTerminalState === "cancelled"
    ? "Отменена"
    : conferenceTerminalState === "closed"
      ? "Закрыта"
      : conferenceEnded
        ? "Завершена"
        : room?.sessionStartAt
          ? durationLabel
          : "Подготовка";
  const focusSecureLabel = conferenceTerminalState === "cancelled"
    ? "Сессия отменена"
    : conferenceTerminalState === "closed"
      ? "Комната закрыта"
      : conferenceEnded
        ? "Сессия завершена"
        : "Сессия активна и защищена";
  const focusEndedBadge = conferenceTerminalState === "cancelled"
    ? "Сессия отменена"
    : conferenceTerminalState === "closed"
      ? "Комната закрыта"
      : "Сессия завершена";
  const focusEndedTitle = conferenceTerminalState === "cancelled"
    ? "Встреча отменена"
    : conferenceTerminalState === "closed"
      ? "Комната закрыта"
      : "Встреча окончена";
  const focusEndedDescription = conferenceTerminalState === "cancelled"
    ? "Подключение недоступно. Эта запись была отменена, поэтому плеер отключен."
    : conferenceTerminalState === "closed"
      ? "Комната больше недоступна для подключения. При необходимости вернитесь в список конференций."
      : "Плеер отключен. Транскрипция появится после обработки Aegis.";
  const focusPrimaryName = room?.psychologist ? psychologistName : clientName;
  const canUseFocusRoom = !roomLoading && !roomError && room;

  return (
    <div className="conference-focus-room">
      <Helmet prioritizeSeoTags>
        <title>Фокус-комната | Бюро Души</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="conference-focus-room__bg" aria-hidden="true" />

      <header className="conference-focus-topbar">
        <div className="conference-focus-topbar__left">
          <button type="button" className="conference-focus-icon-btn" onClick={handleBack} aria-label="Вернуться в кабинет">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="conference-focus-title">
            <strong>{sessionTitle || `Сессия с ${focusPrimaryName}`}</strong>
            <span>{focusTimerLabel}</span>
          </div>
          <div className={`conference-focus-status ${focusStatusTone}`}>
            <i />
            <span>{focusSessionStatusLabel}</span>
          </div>
        </div>
        <div className="conference-focus-topbar__right">
          <div className="conference-focus-controls" role="toolbar" aria-label="Управление конференцией">
            <button type="button" className={`conference-focus-control ${roomDrawer === "chat" ? "is-active" : ""}`} onClick={() => setRoomDrawer(roomDrawer === "chat" ? "" : "chat")} aria-label="Чат">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2h9A3.5 3.5 0 0 1 20 5.5v6A3.5 3.5 0 0 1 16.5 15H9l-5 5V5.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>Чат</span>
              {messageUnreadTotal ? <i>{messageUnreadTotal}</i> : null}
            </button>
            {canCompleteConference ? (
              <button type="button" className={`conference-focus-control ${roomDrawer === "notes" ? "is-active" : ""}`} onClick={() => setRoomDrawer(roomDrawer === "notes" ? "" : "notes")} aria-label="Мои заметки">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 3h8l4 4v14H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8"/><path d="M14 3v5h5M9 13h6M9 17h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                <span>Мои заметки</span>
              </button>
            ) : null}
            <button
              type="button"
              className="conference-focus-control"
              onClick={onCopyInviteLink}
              disabled={!resolvedRoomToken || conferenceEnded}
              aria-label="Пригласить участника"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M10 13a5 5 0 1 1 0-7.1l1 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 11a5 5 0 1 1 0 7.1l-1-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8.5 12h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span>Пригласить</span>
            </button>
            <button
              type="button"
              className={`conference-focus-control ${conferenceEnded ? "" : "is-danger"}`}
              onClick={conferenceEnded ? handleBack : canCompleteConference ? onCompleteSession : handleBack}
              disabled={!conferenceEnded && completingSession}
              aria-label={conferenceEnded ? "Вернуться" : "Завершить"}
            >
              {conferenceEnded ? (
                <svg width="23" height="23" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="23" height="23" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 15.5c4.5-4 9.5-4 14 0l-2.4 2.4a1.6 1.6 0 0 1-1.8.3l-1.7-.8a2.5 2.5 0 0 0-2.2 0l-1.7.8a1.6 1.6 0 0 1-1.8-.3L5 15.5Z" fill="currentColor" />
                </svg>
              )}
              <span>{conferenceEnded ? "Вернуться" : completingSession ? "Завершаем…" : "Завершить"}</span>
            </button>
          </div>
          <span className={`conference-focus-secure ${conferenceEnded ? "is-inactive" : ""}`}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M7 10V7a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
            </svg>
            {focusSecureLabel}
          </span>
        </div>
      </header>

      <main className={`conference-focus-shell ${roomDrawer ? "has-drawer" : ""}`}>
        <section className="conference-focus-stage" aria-label="Видео конференции">
          {roomLoading ? (
            <div className="conference-focus-loading">Открываем защищённую комнату…</div>
          ) : null}
          {!roomLoading && roomError ? (
            <div className="conference-focus-loading is-error">{roomError}</div>
          ) : null}
          {canUseFocusRoom ? (
            <>
              <div className={`conference-focus-video ${conferenceEnded ? "is-ended" : conferenceLocked ? "is-locked" : ""}`}>
                {conferenceEnded ? (
                  <div className="conference-focus-placeholder conference-focus-placeholder--ended">
                    <div className="conference-focus-placeholder__badge">{focusEndedBadge}</div>
                    <h2>{focusEndedTitle}</h2>
                    <p>{focusEndedDescription}</p>
                  </div>
                ) : playerUrl ? (
                  <iframe
                    src={playerUrl}
                    title="Aegis Conference Player"
                    allow="camera; microphone; autoplay; clipboard-read; clipboard-write; fullscreen; display-capture"
                  />
                ) : (
                  <div className="conference-focus-placeholder">
                    <div className="conference-focus-placeholder__ambient" aria-hidden="true" />
                    <div className="conference-focus-persona">
                      <div className="conference-focus-persona__text">
                        <span>{conferenceLocked ? "Комната закрыта" : "Подготовка комнаты"}</span>
                        <strong>{conferenceLocked ? "Подключение откроется ближе к началу встречи" : "Комната подготавливается к подключению"}</strong>
                        <p>{conferenceLocked ? room.aegisJoinBlockedMessage || "Комната откроется ближе к началу сессии." : "Aegis-плеер будет показан здесь после подготовки комнаты."}</p>
                      </div>
                    </div>
                  </div>
                )}

                {conferenceLocked && !conferenceEnded ? (
                  <div className="conference-focus-lock">
                    <strong>Комната пока закрыта</strong>
                    <span>{room.aegisJoinBlockedMessage || "Встреча станет доступна незадолго до начала."}</span>
                    {room.aegisJoinAvailableFromAt ? <em>Доступ: {formatDateTime(room.aegisJoinAvailableFromAt)}</em> : null}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </section>

        {canUseFocusRoom && roomDrawer ? (
          <aside className="conference-focus-drawer" aria-label={roomDrawer === "chat" ? "Чат конференции" : roomDrawer === "notes" ? "Мои заметки" : "Материалы"}>
            <div className="conference-focus-drawer__head">
              <div>
                <h2>{roomDrawer === "chat" ? "Чат" : roomDrawer === "notes" ? "Мои заметки" : "Материалы"}</h2>
                <span>{roomDrawer === "chat" ? "Конфиденциально, только участники сессии" : roomDrawer === "notes" ? "Приватные записи психолога" : "Файлы и ссылки встречи"}</span>
              </div>
              <button type="button" onClick={() => setRoomDrawer("")} aria-label="Закрыть панель">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/></svg>
              </button>
            </div>

            {roomDrawer === "chat" ? (
              <div className="conference-focus-chat">
                {conferenceLocked ? (
                  <div className="conference-focus-empty">Чат станет доступен ближе к началу встречи.</div>
                ) : chatDialogId ? (
                  <div className="conference-focus-chat-theme" data-app-theme="dark">
                    <ChatUI
                      embedded
                      singleDialogMode
                      forcedDialogId={chatDialogId}
                      hideInfoPanel
                      hideDialogSettings
                      onMessagesChange={onConferenceChatMessagesChange}
                    />
                  </div>
                ) : (
                  <div className="conference-focus-empty">Чат для этой конференции пока не подключён.</div>
                )}
                <div className="conference-focus-privacy"><IconLink /> Чат конфиденциален и виден только участникам сессии.</div>
              </div>
            ) : null}

            {roomDrawer === "notes" ? (
              <div className="conference-focus-notes">
                <textarea
                  className="conference-focus-notes__input"
                  rows={5}
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  placeholder="Записать наблюдение, гипотезу или план после сессии…"
                  disabled={conferenceLocked || savingNote}
                />
                <div className="conference-focus-notes__actions">
                  {editingNoteId ? <button type="button" onClick={() => { setEditingNoteId(null); setNoteDraft(""); }} disabled={savingNote}>Отменить</button> : null}
                  <button type="button" onClick={onSaveNote} disabled={conferenceLocked || savingNote || !String(noteDraft || "").trim()}>{savingNote ? "Сохраняем…" : editingNoteId ? "Обновить" : "Сохранить заметку"}</button>
                </div>
                <div className="conference-focus-notes__list">
                  {notesLoading ? <div className="conference-focus-empty">Загружаем заметки…</div> : null}
                  {!notesLoading && notes.length ? notes.map((note) => (
                    <article className="conference-focus-note" key={note.id || note.noteId || note.createdAt || note.content}>
                      <p>{note.content || note.text || note.body}</p>
                      <div>
                        <span>{formatDateTime(note.createdAt || note.updatedAt)}</span>
                        <button type="button" onClick={() => { setEditingNoteId(note.id || note.noteId); setNoteDraft(note.content || note.text || note.body || ""); }}>Изменить</button>
                        <button type="button" onClick={() => onDeleteNote(note.id || note.noteId)} disabled={deletingNoteId === String(note.id || note.noteId)}>Удалить</button>
                      </div>
                    </article>
                  )) : null}
                  {!notesLoading && !notes.length ? <div className="conference-focus-empty">Пока нет заметок. Они будут видны только вам.</div> : null}
                </div>
              </div>
            ) : null}
          </aside>
        ) : null}
      </main>

      <Modal
        open={editOpen && canEditConference}
        className="conference-create-modal-shell"
        onClose={() => {
          if (editSaving) return;
          setEditOpen(false);
        }}
        title="Редактировать конференцию"
        size="sm"
        cardClassName="conference-create-modal"
        actions={(
          <>
            <button type="button" className="conference-ghost-btn" onClick={() => setEditOpen(false)} disabled={editSaving}>
              Отмена
            </button>
            <button type="button" className="conference-primary-btn" onClick={onSaveConferenceEdit} disabled={editSaving}>
              {editSaving ? "Сохранение..." : "Сохранить"}
            </button>
          </>
        )}
      >
        <div className="conference-create-form">
          <label className="b-field">
            <span className="b-field__label">Название сессии</span>
            <input
              className="b-input"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
              placeholder="Например: Индивидуальная сессия"
              maxLength={140}
              disabled={editSaving}
            />
          </label>

          <div className="conference-create-form__row conference-create-form__row--single">
            <label className="b-field conference-create-start-field">
              <span className="b-field__label">Время старта</span>
              <div className="conference-create-start-field__inputs">
                <div className="conference-create-start-field__input-wrap conference-create-start-field__input-wrap--date">
                  <IconCalendar />
                  <input
                    type="text"
                    className="b-input"
                    value={editDateDraft}
                    placeholder="ДД.ММ.ГГГГ"
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(event) => onEditStartDateChange(event.target.value)}
                    disabled={editSaving}
                  />
                </div>
                <div className="conference-create-start-field__input-wrap conference-create-start-field__input-wrap--time">
                  <IconClock />
                  <input
                    type="text"
                    className="b-input"
                    value={editTimeDraft}
                    placeholder="ЧЧ:ММ"
                    inputMode="numeric"
                    maxLength={5}
                    onChange={(event) => onEditStartTimeChange(event.target.value)}
                    disabled={editSaving}
                  />
                </div>
              </div>
              <div className="conference-create-start-field__presets conference-create-start-field__presets--calendar" role="group" aria-label="Быстрый выбор даты">
                <button type="button" className="conference-create-preset-btn" onClick={() => setEditDatePreset(0)} disabled={editSaving}>Сегодня</button>
                <button type="button" className="conference-create-preset-btn" onClick={() => setEditDatePreset(1)} disabled={editSaving}>Завтра</button>
                <button type="button" className="conference-create-preset-btn" onClick={() => setEditDatePreset(7)} disabled={editSaving}>+7 дней</button>
              </div>
              <div className="conference-create-start-field__presets" role="group" aria-label="Быстрый выбор времени">
                <button type="button" className="conference-create-preset-btn" onClick={() => setEditStartPreset(30)} disabled={editSaving}>Через 30 мин</button>
                <button type="button" className="conference-create-preset-btn" onClick={() => setEditStartPreset(60)} disabled={editSaving}>Через 1 час</button>
                <button type="button" className="conference-create-preset-btn" onClick={() => setEditStartPreset(120)} disabled={editSaving}>Через 2 часа</button>
              </div>
              {editStartLabel ? (
                <p className="conference-create-start-field__msk">
                  Выбрано: {editStartLabel}
                </p>
              ) : null}
              <p className="conference-create-start-field__note">
                Время указывается по Москве (МСК, UTC+3). Конференция становится доступна за 30 минут до начала.
              </p>
            </label>
          </div>

          <label className="b-field conference-create-duration-field">
            <span className="b-field__label">Продолжительность</span>
            <div className="conference-create-duration-field__control">
              <select
                className="b-input conference-create-duration-field__select"
                value={String(editDurationMinutes)}
                onChange={(event) => setEditDurationMinutes(Number(event.target.value) || 50)}
                disabled={editSaving}
              >
                <option value="50">50 минут</option>
                <option value="30">30 минут</option>
                <option value="60">60 минут</option>
                <option value="90">90 минут</option>
              </select>
            </div>
          </label>

          <div className="conference-create-type">
            <span className="conference-create-type__label">Тип конференции</span>
            <div className="conference-create-type__grid" role="radiogroup" aria-label="Тип конференции">
              <button
                type="button"
                className={`conference-create-type__option ${editKind === "regular" ? "is-active" : ""}`}
                onClick={() => setEditKind("regular")}
                role="radio"
                aria-checked={editKind === "regular"}
                disabled={editSaving}
              >
                <span className="conference-create-type__icon" aria-hidden="true"><IconSessionVideo /></span>
                <span className="conference-create-type__name">Обычная</span>
                <span className="conference-create-type__desc">Клиент с аккаунтом</span>
              </button>
              <button
                type="button"
                className={`conference-create-type__option ${editKind === "external" ? "is-active is-external" : ""}`}
                onClick={() => setEditKind("external")}
                role="radio"
                aria-checked={editKind === "external"}
                disabled={editSaving}
              >
                <span className="conference-create-type__icon" aria-hidden="true"><IconExternalConference /></span>
                <span className="conference-create-type__name">Внешняя</span>
                <span className="conference-create-type__desc">Без регистрации</span>
              </button>
            </div>
          </div>

          {editKind === "external" ? (
            <div className="conference-create-invite">
              <div className="conference-create-invite__title"><IconExternalConference /> Гостевая ссылка</div>
              <div className="conference-create-invite__row">
                <input
                  className="b-input conference-create-invite__input"
                  readOnly
                  value={guestInviteUrl || "После сохранения обновления ссылка будет доступна здесь"}
                />
                <button
                  type="button"
                  className="conference-link-btn conference-link-btn--copy"
                  disabled={!guestInviteUrl}
                  onClick={async () => {
                    if (!guestInviteUrl) return;
                    try {
                      await navigator.clipboard.writeText(guestInviteUrl);
                      toast.success("Ссылка скопирована");
                    } catch {
                      toast.error("Не удалось скопировать ссылку");
                    }
                  }}
                >
                  Скопировать
                </button>
              </div>
              <p className="conference-create-invite__note">
                Для новых параметров ссылки нажмите «Сохранить». После сохранения ссылка обновится автоматически.
              </p>
            </div>
          ) : null}

          <p className="conference-create-form__hint">
            Длительность текущей сессии: {editDurationMinutes} мин.
          </p>
        </div>
      </Modal>

      <Modal
        open={reviewModalOpen && canLeaveReview}
        className="conference-review-modal-shell"
        onClose={() => {
          if (reviewSubmitting) return;
          dismissReviewPrompt();
        }}
        title="Оценка сессии"
        size="sm"
        cardClassName="conference-review-modal"
        actions={(
          <>
            <button
              type="button"
              className="conference-ghost-btn"
              onClick={dismissReviewPrompt}
              disabled={reviewSubmitting}
            >
              Позже
            </button>
            <button
              type="button"
              className="conference-primary-btn"
              onClick={onSubmitReview}
              disabled={reviewSubmitting || reviewRating < 1}
            >
              {reviewSubmitting ? "Сохраняем..." : "Отправить отзыв"}
            </button>
          </>
        )}
      >
        <div className="conference-review-form">
          <p className="conference-review-form__lead">
            Как прошла встреча? Поставьте оценку психологу и, при желании, добавьте комментарий.
          </p>
          <div className="conference-review-rating" role="radiogroup" aria-label="Оценка сессии от 1 до 5">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                className={`conference-review-rating__star ${reviewRating >= value ? "is-active" : ""}`}
                onClick={() => setReviewRating(value)}
                role="radio"
                aria-checked={reviewRating === value}
                aria-label={`${value} из 5`}
                disabled={reviewSubmitting}
              >
                ★
              </button>
            ))}
          </div>
          <label className="b-field">
            <span className="b-field__label">Комментарий (необязательно)</span>
            <textarea
              className="b-input conference-review-form__textarea"
              value={reviewComment}
              onChange={(event) => setReviewComment(event.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Например: помогли с запросом, было комфортно и понятно"
              disabled={reviewSubmitting}
            />
          </label>
        </div>
      </Modal>

      <Modal
        open={deleteConfirmOpen}
        className="conference-delete-modal-shell"
        onClose={() => {
          if (deletingConference) return;
          setDeleteConfirmOpen(false);
        }}
        title="Удалить конференцию"
        size="sm"
        cardClassName="conference-delete-modal"
        actions={(
          <>
            <button
              type="button"
              className="conference-ghost-btn"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deletingConference}
            >
              Отмена
            </button>
            <button
              type="button"
              className="conference-delete-confirm__submit"
              onClick={onDeleteConference}
              disabled={deletingConference || !canDeleteConference}
            >
              {deletingConference ? "Удаляем..." : "Удалить"}
            </button>
          </>
        )}
      >
        <div className="conference-delete-confirm">
          <p className="conference-delete-confirm__lead">Удалить эту конференцию?</p>
          <p className="conference-delete-confirm__text">
            Конференция будет отменена и исчезнет из списка.
          </p>
        </div>
      </Modal>

      <Modal
        open={inviteModalOpen}
        className="conference-invite-modal-shell"
        onClose={() => setInviteModalOpen(false)}
        title="Приглашение в конференцию"
        size="sm"
        cardClassName="conference-invite-modal"
        actions={(
          <>
            <button type="button" className="conference-ghost-btn" onClick={() => setInviteModalOpen(false)}>
              Отмена
            </button>
            <button
              type="button"
              className="conference-primary-btn"
              onClick={onCopySelectedInviteLink}
              disabled={!selectedInviteUrl}
            >
              Скопировать ссылку
            </button>
          </>
        )}
      >
        <div className="conference-invite-picker">
          <div className="conference-invite-picker__label">Кого приглашаем?</div>
          <div className="conference-invite-picker__grid" role="radiogroup" aria-label="Тип приглашения">
            <button
              type="button"
              className={`conference-invite-picker__option ${inviteAudience === "external" ? "is-active" : ""}`}
              onClick={() => setInviteAudience("external")}
              role="radio"
              aria-checked={inviteAudience === "external"}
            >
              <span className="conference-invite-picker__name">Внешний гость</span>
              <span className="conference-invite-picker__desc">Без аккаунта, вход через гостевую страницу</span>
            </button>
            <button
              type="button"
              className={`conference-invite-picker__option ${inviteAudience === "project" ? "is-active" : ""}`}
              onClick={() => setInviteAudience("project")}
              role="radio"
              aria-checked={inviteAudience === "project"}
            >
              <span className="conference-invite-picker__name">Клиент с проекта</span>
              <span className="conference-invite-picker__desc">Вход через кабинет, при необходимости с авто-присоединением</span>
            </button>
          </div>

          <div className="conference-invite-picker__preview">
            <span>Ссылка:</span>
            <a href={selectedInviteUrl || "#"} target="_blank" rel="noreferrer" onClick={(event) => { if (!selectedInviteUrl) event.preventDefault(); }}>
              {selectedInviteUrl || "Ссылка готовится…"}
            </a>
          </div>
        </div>
      </Modal>
    </div>
  );
}
